/**
 * Taxonomy Tree Editor Component
 * 
 * Hierarchical tree editor for managing taxonomy nodes.
 * Supports adding, editing, and deleting nodes with nested children.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, ChevronDown, Plus, Trash2 } from 'lucide-react';
import { Button, Skeleton } from '@/components/ui';
import apiClient from '@/lib/api-client';
import { toast } from 'sonner';

// =============================================================================
// Types
// =============================================================================

interface TaxonomyNode {
  id: string;
  name: string;
  description: string | null;
  parent_id: string | null;
  search_boost: number;
  children: TaxonomyNode[];
}

interface TaxonomyDetail {
  id: string;
  name: string;
  description: string | null;
  nodes: TaxonomyNode[];
}

interface Props {
  tenantId: string;
  taxonomyId: string;
  taxonomyName: string;
}

// =============================================================================
// Main Component
// =============================================================================

export function TaxonomyTreeEditor({ tenantId, taxonomyId, taxonomyName }: Props) {
  const queryClient = useQueryClient();

  const { data: taxonomy, isLoading } = useQuery<TaxonomyDetail>({
    queryKey: ['taxonomy', tenantId, taxonomyId],
    queryFn: async () => {
      // Don't include tenantId in path — apiClient.buildUrl() adds /tenants/{id}/ automatically
      const response = await apiClient.get(`/api/v1/taxonomy/tree`, { params: { root_id: taxonomyId } });
      // Map tree response to TaxonomyDetail format
      const treeData = response.data as { nodes?: TaxonomyNode[]; data?: TaxonomyDetail };
      if (treeData.nodes) {
        return { id: taxonomyId, name: taxonomyName, description: null, nodes: treeData.nodes } as TaxonomyDetail;
      }
      return treeData.data || treeData as unknown as TaxonomyDetail;
    },
  });

  const addNodeMutation = useMutation({
    mutationFn: (data: { name: string; parent_id: string | null }) =>
      apiClient.post('/api/v1/taxonomy/nodes', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxonomy', tenantId, taxonomyId] });
      queryClient.invalidateQueries({ queryKey: ['taxonomies', tenantId] });
      toast.success('Node added successfully');
    },
    onError: () => {
      toast.error('Failed to add node');
    },
  });

  const deleteNodeMutation = useMutation({
    mutationFn: (nodeId: string) =>
      apiClient.delete(`/api/v1/taxonomy/nodes/${nodeId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxonomy', tenantId, taxonomyId] });
      queryClient.invalidateQueries({ queryKey: ['taxonomies', tenantId] });
      toast.success('Node deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete node');
    },
  });

  const handleAddNode = (parentId: string | null) => {
    const name = prompt('Enter node name:');
    if (name?.trim()) {
      addNodeMutation.mutate({ name: name.trim(), parent_id: parentId });
    }
  };

  const handleDeleteNode = (nodeId: string) => {
    if (confirm('Delete this node and all children?')) {
      deleteNodeMutation.mutate(nodeId);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const nodes = taxonomy?.nodes || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {taxonomyName}
        </h2>
        <Button
          size="sm"
          onClick={() => handleAddNode(null)}
          disabled={addNodeMutation.isPending}
          leftIcon="plus"
        >
          Add Root Node
        </Button>
      </div>

      <div className="border rounded-lg p-4 bg-card">
        {nodes.length > 0 ? (
          <TreeNodeList
            nodes={nodes}
            onAddChild={handleAddNode}
            onDelete={handleDeleteNode}
          />
        ) : (
          <p className="text-muted-foreground text-center py-8">
            No nodes yet. Add a root node to build your taxonomy.
          </p>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Tree Node List Component
// =============================================================================

function TreeNodeList({
  nodes,
  depth = 0,
  onAddChild,
  onDelete,
}: {
  nodes: TaxonomyNode[];
  depth?: number;
  onAddChild: (parentId: string) => void;
  onDelete: (nodeId: string) => void;
}) {
  return (
    <div className="space-y-1">
      {nodes.map((node) => (
        <TreeNodeItem
          key={node.id}
          node={node}
          depth={depth}
          onAddChild={onAddChild}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

// =============================================================================
// Tree Node Item Component
// =============================================================================

function TreeNodeItem({
  node,
  depth,
  onAddChild,
  onDelete,
}: {
  node: TaxonomyNode;
  depth: number;
  onAddChild: (parentId: string) => void;
  onDelete: (nodeId: string) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div>
      <div
        className="flex items-center gap-2 p-2 rounded hover:bg-muted group"
        style={{ paddingLeft: `${depth * 24 + 8}px` }}
      >
        <button
          type="button"
          className="w-4 h-4 flex items-center justify-center text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded(!expanded)}
          disabled={!hasChildren}
        >
          {hasChildren ? (
            expanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )
          ) : (
            <span className="w-4" />
          )}
        </button>

        <span className="flex-1 text-gray-900 dark:text-gray-100">
          {node.name}
        </span>

        {node.search_boost !== 1.0 && (
          <span className="text-xs text-muted-foreground">
            ×{node.search_boost}
          </span>
        )}

        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => {
              e.stopPropagation();
              onAddChild(node.id);
            }}
            title="Add child node"
          >
            <Plus className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-destructive hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(node.id);
            }}
            title="Delete node"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {hasChildren && expanded && (
        <TreeNodeList
          nodes={node.children}
          depth={depth + 1}
          onAddChild={onAddChild}
          onDelete={onDelete}
        />
      )}
    </div>
  );
}
